import {
  isSamePipelineStatus,
  normalizeCandidateStatusKey,
} from '@/utils/candidate-status-flow'

/** Default Call Analytics date preset: last 7 calendar days, inclusive of today. */
export const DEFAULT_DATE_PRESET = 'last_7'

/**
 * Inclusive last-7-days start for a YYYY-MM-DD "today" (today and the 6 days before).
 */
export function last7InclusiveStart(todayYmd) {
  if (!todayYmd) return ''
  const start = Date.parse(`${todayYmd}T00:00:00Z`)
  if (!Number.isFinite(start)) return ''
  const d = new Date(start)
  d.setUTCDate(d.getUTCDate() - 6)
  return d.toISOString().slice(0, 10)
}

/** Inclusive calendar days between YYYY-MM-DD start and end. */
export function inclusiveDaySpan(startDate, endDate) {
  if (!startDate || !endDate) return 1
  const start = Date.parse(`${startDate}T00:00:00Z`)
  const end = Date.parse(`${endDate}T00:00:00Z`)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return 1
  return Math.round((end - start) / 86400000) + 1
}

/**
 * Last 7 days or shorter — no extra filters (page default).
 * Longer ranges — at least 2, so the full-period live fetch can finish.
 */
export function requiredNarrowingCount(spanDays) {
  if (spanDays <= 7) return 0
  return 2
}

const NARROWING_FIELDS = [
  { key: 'status', label: 'Call Status' },
  { key: 'direction', label: 'Direction' },
  { key: 'client', label: 'Client' },
  { key: 'candidateStatus', label: 'Candidate Status' },
  { key: 'manager', label: 'Manager', needs: 'manager' },
  { key: 'recruiter', label: 'Recruiter', needs: 'recruiter' },
  { key: 'vertical', label: 'Vertical' },
  { key: 'category', label: 'Category' },
]

/** Jobs-config defaults (GET /admin/config/jobs) — not invoice Recruitment/Contractual. */
export const DEFAULT_JOB_VERTICALS = Object.freeze(['Service Industry', 'Infra Industry'])
export const DEFAULT_JOB_CATEGORIES = Object.freeze(['White Collar', 'Blue Collar'])

/** Filters-modal value for empty / unknown / no candidate pipeline status. */
export const NO_CANDIDATE_STATUS_FILTER = 'none'

/**
 * ATS pipeline statuses (Candidates list + call-outcome / later-stage flow).
 * Extra values from loaded rows are merged in buildCandidateStatusFilterOptions.
 */
export const DEFAULT_CANDIDATE_STATUSES = Object.freeze([
  'UploadedBySourcer',
  'UploadedByRecruiter',
  'AssignedByMassMail',
  'AssignedByATSSourcer',
  'AssignedByATSMassMailer',
  'Wrong Number',
  'Not Answered',
  'Not Reachable',
  'Not Interested',
  'Not Relevant',
  'Call Back',
  'Interested',
  'ProfileShared',
  'ReviewReject',
  'Initiated',
  'ScreenReject',
  'ScreenSelect',
  'Duplicate',
  'Inprocess',
  'OnHold',
  'Interview',
  'L1 Select',
  'L2 Select',
  'Final Select',
  'Shortlisted',
  'Selected',
  'Draft Offer',
  'Offered',
  'NotOffered',
  'Joined',
  'OfferDrop',
  'Rejected',
  'Early Attrition',
  'Attrition',
  'Confirmed',
  'LOI Issued',
  'Document Submitted',
  'Drop',
])

export function normalizeJobFilterValue(value) {
  return String(value || '').trim().toLowerCase()
}

export function isActiveEmployeeStatus(user) {
  if (!user || typeof user !== 'object') return false
  const s = user.status != null ? user.status : (user.employee_status != null ? user.employee_status : user.is_active)
  return s === 'Active' || s === 'active' || s === true || s === 'ACTIVE'
}

function firstNonEmpty(...vals) {
  for (let i = 0; i < vals.length; i += 1) {
    const v = vals[i]
    if (v == null) continue
    const s = String(v).trim()
    if (s) return s
  }
  return ''
}

/** Normalize a client name or id for equality (trim, collapse space, lower). */
export function normalizeClientKey(value) {
  return String(value == null ? '' : value).replace(/\s+/g, ' ').trim().toLowerCase()
}

function clientLabelFromValue(value) {
  if (value == null || value === '') return ''
  if (typeof value === 'object') {
    return firstNonEmpty(
      value.title,
      value.client_name,
      value.clientName,
      value.name,
      value.client,
    )
  }
  return String(value).trim()
}

function clientIdFromValue(value) {
  if (value == null || value === '') return ''
  if (typeof value === 'object') {
    return firstNonEmpty(value.id, value.client_id, value.clientId, value.clientID)
  }
  const s = String(value).trim()
  return s
}

/**
 * Names + ids that mean the selected Filters → Client value.
 * Resolves master-list title ↔ id so Team/My rows can match either field.
 */
export function resolveClientFilterTarget(client, clientsList) {
  const names = new Set()
  const ids = new Set()
  const want = normalizeClientKey(client)
  if (!want || want === 'all') return { names, ids, want: '' }
  names.add(want)
  ids.add(want)
  ;(Array.isArray(clientsList) ? clientsList : []).forEach((item) => {
    if (item == null) return
    const label = normalizeClientKey(typeof item === 'string' ? item : clientLabelFromValue(item))
    const id = typeof item === 'string' ? '' : normalizeClientKey(clientIdFromValue(item))
    if (label === want || (id && id === want)) {
      if (label) names.add(label)
      if (id) ids.add(id)
    }
  })
  return { names, ids, want }
}

function collectCallClientNames(call, jobMeta = {}, candidateJobs = []) {
  const names = []
  if (!call) return names
  const push = (value) => {
    const label = clientLabelFromValue(value)
    if (label) names.push(label)
  }
  push(call.clientName)
  push(call.client_name)
  push(call.client)
  push(call._clientNameLower)
  if (jobMeta) push(jobMeta.client || jobMeta.client_name || jobMeta.clientName)
  ;(Array.isArray(candidateJobs) ? candidateJobs : []).forEach((job) => {
    if (!job) return
    push(job.client || job.client_name || job.clientName)
  })
  return names
}

function collectCallClientIds(call, jobMeta = {}) {
  const ids = []
  if (!call) return ids
  const push = (value) => {
    const id = clientIdFromValue(value)
    if (id) ids.push(id)
  }
  push(call.clientId)
  push(call.client_id)
  push(call.clientID)
  push(call.client)
  if (jobMeta) {
    push(jobMeta.client_id)
    push(jobMeta.clientId)
    push(jobMeta.client)
  }
  return ids
}

/**
 * True when a call belongs to the selected client (name or id, any common field).
 * Used on the live Team / My / History / KPI list — API has no reliable client param.
 */
export function callMatchesClientTarget(call, target, jobMeta = {}, candidateJobs = []) {
  if (!target || !target.want) return true
  const names = collectCallClientNames(call, jobMeta, candidateJobs)
  for (let i = 0; i < names.length; i += 1) {
    if (target.names.has(normalizeClientKey(names[i]))) return true
  }
  const ids = collectCallClientIds(call, jobMeta)
  for (let i = 0; i < ids.length; i += 1) {
    if (target.ids.has(normalizeClientKey(ids[i]))) return true
  }
  return false
}

/** Work-tracker line (per client/position) vs the same Client filter target. */
export function trackerLineMatchesClientTarget(item, target) {
  if (!target || !target.want) return true
  if (!item || typeof item !== 'object') return false
  const names = [
    item.client,
    item.Client,
    item.client_name,
    item.clientName,
    item.ClientName,
    item.client_title,
    item.ClientTitle,
  ]
  for (let i = 0; i < names.length; i += 1) {
    const key = normalizeClientKey(clientLabelFromValue(names[i]))
    if (key && target.names.has(key)) return true
  }
  const ids = [item.client_id, item.clientId, item.ClientId, item.ClientID]
  for (let i = 0; i < ids.length; i += 1) {
    const key = normalizeClientKey(clientIdFromValue(ids[i]))
    if (key && target.ids.has(key)) return true
  }
  return false
}

/** Job vertical on a call row / job map — ignore call-analysis outcome categories. */
export function resolveCallJobVertical(call = {}, jobMeta = {}) {
  return firstNonEmpty(
    call._jobVertical,
    call.jobVertical,
    call.job_vertical,
    jobMeta.vertical,
    jobMeta.job_vertical,
    call.job && typeof call.job === 'object' ? call.job.vertical : '',
  )
}

export function resolveCallJobCategory(call = {}, jobMeta = {}) {
  return firstNonEmpty(
    call._jobCategory,
    call.jobCategory,
    call.job_category,
    jobMeta.category,
    jobMeta.job_category,
    call.job && typeof call.job === 'object' ? call.job.category : '',
  )
}

export function callMatchesVerticalCategory(call, filters = {}, jobMeta = {}) {
  const wantV = filters.vertical && filters.vertical !== 'all'
    ? normalizeJobFilterValue(filters.vertical)
    : ''
  const wantC = filters.category && filters.category !== 'all'
    ? normalizeJobFilterValue(filters.category)
    : ''
  if (!wantV && !wantC) return true
  if (wantV && normalizeJobFilterValue(resolveCallJobVertical(call, jobMeta)) !== wantV) return false
  if (wantC && normalizeJobFilterValue(resolveCallJobCategory(call, jobMeta)) !== wantC) return false
  return true
}

function firstMeaningfulCandidateStatus(...vals) {
  for (let i = 0; i < vals.length; i += 1) {
    const s = firstNonEmpty(vals[i])
    if (!s || s === '—' || s === '-') continue
    return s
  }
  return ''
}

/**
 * Pipeline status on a History / list row.
 * Same aliases as mapEnrichedCallRow + getCandidateStatus (row first, then job match).
 */
export function resolveCallCandidateStatus(call = {}, jobMatch = null) {
  const fromRow = firstMeaningfulCandidateStatus(
    call.candidateStatus,
    call.candidate_status,
    call.pipeline_status,
    call.current_status,
    call.currentStatus,
  )
  if (fromRow) return fromRow
  if (jobMatch && typeof jobMatch === 'object') {
    return firstMeaningfulCandidateStatus(
      jobMatch.candidateStatus,
      jobMatch.pipeline_status,
      jobMatch.candidate_status,
      jobMatch.current_status,
      jobMatch.currentStatus,
    )
  }
  return ''
}

/**
 * History / Team / Org / KPI / download — Filters → Candidate Status (single-select).
 * `none` = empty / — / no candidate on the row (after the same alias resolve).
 */
export function callMatchesCandidateStatusFilter(call, statusFilter, jobMatch = null) {
  if (!statusFilter || statusFilter === 'all') return true
  const resolved = resolveCallCandidateStatus(call, jobMatch)
  if (statusFilter === NO_CANDIDATE_STATUS_FILTER) return !resolved
  return isSamePipelineStatus(resolved, statusFilter)
}

/** All + No status + ATS list + extras from the current page / applied value. */
export function buildCandidateStatusFilterOptions(extraStatuses = []) {
  const seen = new Set()
  const labels = []
  const add = (name) => {
    const n = String(name || '').trim()
    if (!n || n === '—' || n === '-') return
    const key = normalizeCandidateStatusKey(n)
    if (!key || key === 'all' || key === 'none' || key === 'nostatus') return
    if (seen.has(key)) return
    seen.add(key)
    labels.push(n)
  }
  DEFAULT_CANDIDATE_STATUSES.forEach(add)
  ;(Array.isArray(extraStatuses) ? extraStatuses : []).forEach(add)
  labels.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
  return [
    { value: 'all', text: 'All Candidate Statuses' },
    { value: NO_CANDIDATE_STATUS_FILTER, text: 'No status' },
    ...labels.map(n => ({ value: n, text: n })),
  ]
}

const ANSWERED_STATUS_KEYS = new Set([
  'answered', 'completed', 'connected', 'in-progress', 'in_progress', 'inprogress',
])

const NOT_ANSWERED_STATUS_KEYS = new Set([
  'no-answer', 'no_answer', 'noanswered', 'noanswer', 'not answered', 'notanswered',
  'not_answered', 'not-answered', 'not_rang', 'not-rang', 'notrang', 'not connected',
  'not-connected', 'notconnected', 'missed', 'missed-call', 'missed_call',
])

const FAILED_STATUS_KEYS = new Set([
  'failed', 'temporaryfailed', 'temporary-failed', 'temporary_failed', 'temporary failed',
])

const QUEUED_STATUS_KEYS = new Set([
  'queued', 'scheduled', 'pending', 'initiated', 'calling', 'ringing',
])

/** Raw Exotel / display labels → a compact key (no-answer, not answered, Not_Answered). */
export function normalizeCallStatusKey(value) {
  return String(value == null ? '' : value).trim().toLowerCase().replace(/[_]+/g, '-')
}

function compactCallStatusKey(value) {
  return normalizeCallStatusKey(value).replace(/[\s-]+/g, '')
}

function pickCallFilterStatus(call) {
  if (!call || typeof call !== 'object') return ''
  const raw = normalizeCallStatusKey(call._rawStatus)
  if (raw) return raw
  return normalizeCallStatusKey(call.status || call.Status || call.call_status)
}

/**
 * Bucket used by Filters → Call Status.
 * Answered = connected / completed / answered only — never NA, queued, missed, failed, busy.
 */
export function callStatusFilterFamily(raw) {
  const key = normalizeCallStatusKey(raw)
  if (!key) return 'other'
  const compact = compactCallStatusKey(key)
  if (
    ANSWERED_STATUS_KEYS.has(key)
    || compact === 'answered'
    || compact === 'completed'
    || compact === 'connected'
    || compact === 'inprogress'
  ) return 'answered'
  if (
    NOT_ANSWERED_STATUS_KEYS.has(key)
    || compact === 'noanswer'
    || compact === 'notanswered'
    || compact === 'notrang'
    || compact === 'missed'
    || compact === 'missedcall'
    || compact === 'notconnected'
  ) return 'not_answered'
  if (
    FAILED_STATUS_KEYS.has(key)
    || compact === 'failed'
    || compact.startsWith('temporaryfailed')
  ) return 'failed'
  if (key === 'busy' || compact === 'busy') return 'busy'
  if (QUEUED_STATUS_KEYS.has(key) || QUEUED_STATUS_KEYS.has(compact)) return 'queued'
  return 'other'
}

/**
 * History / Team / Org / KPI / download — same Call Status rule.
 * Do not pass inbound-missed rows just because they are inbound.
 */
export function callMatchesStatusFilter(call, statusFilter) {
  if (!statusFilter || statusFilter === 'all') return true
  const family = callStatusFilterFamily(pickCallFilterStatus(call))
  if (statusFilter === 'answered') return family === 'answered'
  if (statusFilter === 'not_answered') return family === 'not_answered'
  if (statusFilter === 'failed') return family === 'failed'
  if (statusFilter === 'busy') return family === 'busy'
  return true
}

function pickCallFilterDirection(call) {
  if (!call || typeof call !== 'object') return ''
  // Mapped History rows always set _rawDirection (inbound-miss may force "inbound").
  // Do not fall back to display direction — normalizeDirection defaults empty to Outbound.
  if (Object.prototype.hasOwnProperty.call(call, '_rawDirection')) {
    return String(call._rawDirection == null ? '' : call._rawDirection).trim()
  }
  return firstNonEmpty(call.Direction, call.direction)
}

/**
 * Bucket used by Filters → Direction.
 * Outbound = outbound / outgoing / out / click-to-call only.
 * Inbound = inbound / incoming / in only. Never infer from missed/NA.
 */
export function callDirectionFilterFamily(raw) {
  const key = String(raw == null ? '' : raw).trim().toLowerCase()
  if (!key) return 'other'
  const compact = key.replace(/[\s_-]+/g, '')
  if (key === 'in' || compact === 'in') return 'inbound'
  if (key === 'incoming' || compact === 'incoming' || compact.startsWith('incoming')) return 'inbound'
  if (key.includes('inbound') || compact.includes('inbound')) return 'inbound'
  if (key === 'out' || compact === 'out') return 'outbound'
  if (key === 'outgoing' || compact === 'outgoing' || compact.startsWith('outgoing')) return 'outbound'
  if (key.includes('outbound') || compact.includes('outbound')) return 'outbound'
  if (
    compact.includes('clicktocall')
    || key.includes('click-to-call')
    || key.includes('click_to_call')
  ) return 'outbound'
  return 'other'
}

/**
 * History / Team / Org / KPI / download — same Direction rule.
 * Apply before any inbound-missed keep. Missed inbound must not pass Outbound.
 */
export function callMatchesDirectionFilter(call, directionFilter) {
  if (!directionFilter || directionFilter === 'all') return true
  const family = callDirectionFilterFamily(pickCallFilterDirection(call))
  if (directionFilter === 'inbound') return family === 'inbound'
  if (directionFilter === 'outbound') return family === 'outbound'
  return true
}

function isSelected(filters, key) {
  const val = filters && filters[key]
  return val != null && val !== '' && val !== 'all'
}

export function selectedNarrowingFilters(filters = {}, options = {}) {
  const includeManager = options.includeManager !== false
  const includeRecruiter = options.includeRecruiter !== false
  return NARROWING_FIELDS.filter((item) => {
    if (item.needs === 'manager' && !includeManager) return false
    if (item.needs === 'recruiter' && !includeRecruiter) return false
    return isSelected(filters, item.key)
  }).map((item) => item.label)
}

export function availableNarrowingLabels(options = {}) {
  const includeManager = options.includeManager !== false
  const includeRecruiter = options.includeRecruiter !== false
  return NARROWING_FIELDS.filter((item) => {
    if (item.needs === 'manager' && !includeManager) return false
    if (item.needs === 'recruiter' && !includeRecruiter) return false
    return true
  }).map((item) => item.label)
}

export function describeWideRangeFilterError(filters = {}, options = {}) {
  const span = inclusiveDaySpan(filters.startDate, filters.endDate)
  const required = requiredNarrowingCount(span)
  if (required <= 0) return ''
  const selected = selectedNarrowingFilters(filters, options)
  if (selected.length >= required) return ''
  const labels = availableNarrowingLabels(options)
  const more = required - selected.length
  return `For a ${span}-day range, select at least ${required} of: ${labels.join(', ')}. `
    + `You have ${selected.length || 'none'} — add ${more} more so every matching call can load.`
}
